import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Trash2, Loader2, Send, ShoppingBag } from "lucide-react";
import { useCart } from "../lib/cart";
import { resolveImg, api } from "../lib/api";

const inputCls = "w-full bg-white border border-vr-border rounded-lg px-4 py-2.5 text-vr-ink placeholder:text-vr-dim/60 focus:outline-none focus:border-vr-brand focus:ring-2 focus:ring-vr-brand/15";

export const CartDrawer = () => {
  const { items, open, setOpen, remove, updateQty, clear } = useCart();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please enter your name and email."); return; }
    if (!items.length) { toast.error("Your quote list is empty."); return; }
    setLoading(true);
    try {
      await api.post("/quotes/cart", { ...form, items: items.map(({ sku, product_name, quantity }) => ({ sku, product_name, quantity })) });
      toast.success(`Quote sent — ${items.length} item(s). Our team will be in touch shortly.`);
      clear(); setForm({ name: "", email: "", phone: "", company: "", message: "" }); setShowForm(false); setOpen(false);
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="cart-drawer" className="fixed inset-0 z-[100] flex justify-end bg-vr-navy/60 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <motion.div initial={{ x: 480 }} animate={{ x: 0 }} exit={{ x: 480 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white h-full flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-vr-border">
              <div className="flex items-center gap-2 font-display font-bold text-lg text-vr-ink"><ShoppingBag size={20} className="text-vr-brand" /> Your Quote ({items.length})</div>
              <button data-testid="cart-close" onClick={() => setOpen(false)} className="text-vr-dim hover:text-vr-ink"><X size={22} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {items.length === 0 && <div className="text-center py-16 text-vr-dim"><ShoppingBag size={36} className="mx-auto mb-3 text-vr-dim/40" /><p>Your quote list is empty.</p><p className="text-xs mt-1">Add products from the catalogue to request a quote.</p></div>}
              {items.map((it) => (
                <div key={it.sku} data-testid={`cart-item-${it.sku}`} className="flex gap-3 rounded-xl border border-vr-border bg-vr-bg2 p-3">
                  <div className="w-16 h-16 rounded-lg bg-[#e6f4fc] flex items-center justify-center overflow-hidden shrink-0">
                    {it.image && <img src={resolveImg(it.image)} alt="" className="max-w-full max-h-full object-contain" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono text-vr-brand">{it.sku}</div>
                    <div className="text-sm text-vr-ink font-medium line-clamp-2">{it.product_name}</div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <label className="text-xs text-vr-dim">Qty</label>
                      <input value={it.quantity} onChange={(e) => updateQty(it.sku, e.target.value)} data-testid={`cart-qty-${it.sku}`} className="w-24 text-sm bg-white border border-vr-border rounded px-2 py-1 focus:outline-none focus:border-vr-brand" />
                      <button onClick={() => remove(it.sku)} data-testid={`cart-remove-${it.sku}`} className="ml-auto text-vr-dim hover:text-red-500 transition-colors"><Trash2 size={15} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {items.length > 0 && (
              <div className="border-t border-vr-border p-5">
                {!showForm ? (
                  <div className="space-y-2">
                    <button data-testid="cart-request-quote" onClick={() => setShowForm(true)} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-vr-brand text-white font-semibold hover:bg-vr-branddark transition-colors">
                      <Send size={17} /> Request Quote for {items.length} item{items.length !== 1 ? "s" : ""}
                    </button>
                    <button onClick={clear} className="w-full text-sm text-vr-dim hover:text-red-500">Clear list</button>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="cart-quote-form" className="grid grid-cols-2 gap-3">
                    <input data-testid="cart-name" className={`col-span-2 ${inputCls}`} placeholder="Your name *" value={form.name} onChange={set("name")} />
                    <input data-testid="cart-email" type="email" className={`col-span-2 ${inputCls}`} placeholder="Email *" value={form.email} onChange={set("email")} />
                    <input data-testid="cart-phone" className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
                    <input data-testid="cart-company" className={inputCls} placeholder="Company" value={form.company} onChange={set("company")} />
                    <textarea data-testid="cart-message" rows={2} className={`col-span-2 ${inputCls} resize-none`} placeholder="Any notes…" value={form.message} onChange={set("message")} />
                    <button type="submit" data-testid="cart-submit" disabled={loading} className="col-span-2 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-vr-brand text-white font-semibold hover:bg-vr-branddark transition-colors disabled:opacity-60">
                      {loading ? <Loader2 className="animate-spin" size={17} /> : <Send size={17} />}{loading ? "Sending…" : "Send Quote Request"}
                    </button>
                  </form>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
